import React, { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import cn from 'classnames';
import { Icon, IconProps } from '../icon/icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    /**
     * FROK button type (Primary, Secondary, Tertiary or Inverted)
     */
    mode?: 'primary' | 'secondary' | 'tertiary' | 'integrated';
    /**
     * Label inside the button, can also contain React elements
     */
    label?: ReactNode;
    fixedWidth?: boolean;
    /**
     * String bound to button's "data-frok-action" attribute
     */
    action?: string;
    /**
     * Either the name of the icon as string, or full IconProps that will be forwarded to icon
     */
    icon?: string | IconProps;
    children?: ReactNode;
    isUiIcon?: boolean;
}

/**
 *
 * @param   {string} icon           Icon to Display
 * @param   {isUiIcon}  boolean           whether or not it's an icon from the UI font or not
 * @param   {boolean} fixedWidth    Fixed or not fixed width
 * @param   {string} action         Name of the action this button should be used for
 *
 * @description
 * Representation of buttons
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
    const {
        type = 'button',
        mode = 'primary',
        label,
        icon,
        fixedWidth,
        action = null,
        className,
        children,
        isUiIcon,
        ...rest
    } = props;
    const buttonClass = cn(
        'a-button',
        {
            [`a-button--${mode}`]: mode,
            '-fixed': fixedWidth,
            '-without-icon': (label && !icon) || (!label && !icon),
            '-without-label': (icon && !label) || (!icon && !label),
        },
        className,
    );

    const iconProps =
        typeof icon === 'string' ? { iconName: icon } : { ...icon };
    iconProps.className = cn('a-button__icon', iconProps.className);

    return (
        <button
            ref={ref}
            className={buttonClass}
            data-frok-action={action}
            type={type}
            {...rest}
        >
            {icon && !children && <Icon {...iconProps} />}
            {label && !children && (
                <div className="a-button__label">{label}</div>
            )}
            {children ? children : null}
        </button>
    );
});

Button.displayName = 'Button';

export { Button };
